import { Box, createStyles, Divider, Group, Stack, Text } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { formatCurrency } from "../utils/misc.util";

const useStyles = createStyles((theme) => ({
  container: {
    borderRadius: "var(--codex-radius-base)",
    padding: "var(--codex-spacing-4)",
    backgroundColor: theme.other.semanticColors.bg.infoSubtlest,
  },
  header: {
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.bold,
    fontSize: "var(--codex-font-size-lg)",
    lineHeight: "var(--codex-font-line-height-normal)",
    color: theme.other.semanticColors.textSubtle,
  },
  bodyText: {
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.normal,
    fontSize: "var(--codex-font-size-base)",
    lineHeight: "var(--codex-font-line-height-normal)",
    color: theme.other.semanticColors.textBold,
  },
  bodySemiBoldText: {
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.semibold,
    fontSize: "var(--codex-font-size-base)",
    lineHeight: "var(--codex-font-line-height-normal)",
    color: theme.other.semanticColors.textBold,
  },
  subtitle: {
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.normal,
    fontSize: "var(--codex-font-size-sm)",
    lineHeight: "var(--codex-font-line-height-normal)",
    color: theme.other.semanticColors.textBold,
  },
  headerDividerLine: {
    alignSelf: "stretch",
    borderColor: theme.other.semanticColors.headerDivider,
    marginTop: "var(--codex-spacing-4)",
    marginBottom: "var(--codex-spacing-4)",
  },
  dividerLine: {
    alignSelf: "stretch",
    borderColor: theme.other.semanticColors.divider,
    marginTop: "var(--codex-spacing-4)",
    marginBottom: "var(--codex-spacing-4)",
  },
}));

interface RpaPriceEstimateTableProps {
  basePrice: number;
  additionalPagePrice: number;
  minPageCount: number;
  quantity: number;
  pageCount: number;
}

export const RpaPriceEstimateTable: React.FC<RpaPriceEstimateTableProps> = ({
  basePrice,
  additionalPagePrice,
  minPageCount,
  quantity,
  pageCount,
}) => {
  const { classes } = useStyles();

  const additionalPageCost = additionalPagePrice * (pageCount - minPageCount);
  const totalEstimatedPrice = (basePrice + additionalPageCost) * quantity;

  return (
    <Box className={classes.container}>
      <Text className={classes.header}>Price Estimate</Text>
      <Divider className={classes.headerDividerLine} />
      <Stack spacing={2}>
        <Group position={"apart"}>
          <Text className={classes.bodyText}>First {minPageCount} pages per copy</Text>
          <Text className={classes.bodyText}>
            {formatCurrency(GraphQL.SupportedCurrency.Usd, basePrice, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Group>
        <Group position={"apart"} sx={{ flexWrap: "nowrap" }}>
          <Text className={classes.bodyText}>Add'l pages per copy</Text>
          <Text className={classes.bodyText}>
            +{" "}
            {formatCurrency(GraphQL.SupportedCurrency.Usd, additionalPageCost, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Group>
        <Group position={"apart"}>
          <Text className={classes.bodyText}>Quantity</Text>
          <Text className={classes.bodyText}>x {quantity}</Text>
        </Group>
      </Stack>
      <Divider className={classes.dividerLine} />
      <Stack spacing={2}>
        <Group position={"apart"}>
          <Text className={classes.bodySemiBoldText}>Total Estimated Price</Text>
          <Text className={classes.bodySemiBoldText}>
            {formatCurrency(GraphQL.SupportedCurrency.Usd, totalEstimatedPrice, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Group>
        <Text className={classes.subtitle}>Excludes shipping, taxes, and duties</Text>
      </Stack>
    </Box>
  );
};
